import { StepConfigItem } from '~/Interfaces';
import { CountryCode, FlowTypes } from '~/enums';
import { ApplicationOverview } from './ApplicationOverview/ApplicationOverview';
import BankConnection from './BankConnection/BankConnectionStep';
import { CardOptIn } from './CardOptIn/CardOptIn';
import CompanySelector from './CompanySelector/CompanySelector';
import ConnectToBank from './ConnectToBank/ConnectToBank';
import ConnectToEskat from './ConnectToEskat/ConnectToEskat';
import FinancialInformation from './FinancialInformation/FinancialInformation';
import LoanPurpose from './LoanPurpose/LoanPurpose';
import { PersonalInformation } from './PersonalInformation/PersonalInformation';
import SelectGuarantor from './SelectGuarantor/SelectGuarantor';
import { StartApplication } from './StartApplication/StartApplication';
import { UploadDocuments } from './UploadDocuments/UploadDocuments';
import { Checkpoint } from './Checkpoint/Checkpoint';
import { LegalQuestions } from './LegalQuestions/LegalQuestions';

export const StartApplicationStep: StepConfigItem = {
  component: StartApplication,
  name: 'start_application_step',
};

export const CompanySelectorStep: StepConfigItem = {
  component: CompanySelector,
  name: 'company_selector_step',
};

export const PersonalInformationStep: StepConfigItem = {
  component: PersonalInformation,
  name: 'personal_information_step',
};

const LoanPurposeStep: StepConfigItem = {
  component: LoanPurpose,
  name: 'loan_purpose_step',
};

const CardOptInStep: StepConfigItem = {
  component: CardOptIn,
  name: 'card_opt_in_step',
  hideNavbar: true,
  hideInProgressStepper: true,
};

const CheckpointStep: StepConfigItem = {
  component: Checkpoint,
  name: 'checkpoint_step',
  hideInProgressStepper: true,
  hideNavbar: true,
};

const ApplicationOverviewStep: StepConfigItem = {
  component: ApplicationOverview,
  name: 'application_overview_step',
  hideInProgressStepper: true,
};

const SelectGuarantorStep: StepConfigItem = {
  component: SelectGuarantor,
  name: 'guarantor_selector_step',
};

const KlarnaKosmaBankIntegrationStep: StepConfigItem = {
  component: ConnectToBank,
  name: 'connect_to_bank_through_klarna_kosma_step',
};

const BankConnectionStep: StepConfigItem = {
  component: BankConnection,
  name: 'connect_to_bank_step',
};

const FinancialInformationStep: StepConfigItem = {
  component: FinancialInformation,
  name: 'financial_information_step',
};

const UploadDocumentsStep: StepConfigItem = {
  component: UploadDocuments,
  name: 'upload_documents_step',
};

const EskatStep: StepConfigItem = {
  component: ConnectToEskat,
  name: 'connect_to_eskat_step',
};

const LegalQuestionsStep: StepConfigItem = {
  component: LegalQuestions,
  name: 'legal_questions_step',
};

export const stepConfigPerMarketFlowType: Record<
  CountryCode,
  Record<FlowTypes, StepConfigItem[]>
> = {
  [CountryCode.SE]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.FI]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.NO]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.DK]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      EskatStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      EskatStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.BE]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      LegalQuestionsStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      LegalQuestionsStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      KlarnaKosmaBankIntegrationStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.NL]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
  },
  [CountryCode.DE]: {
    [FlowTypes.Authenticated]: [
      CompanySelectorStep,
      LoanPurposeStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      FinancialInformationStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
    [FlowTypes.Manual]: [
      CompanySelectorStep,
      LoanPurposeStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      FinancialInformationStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
  },
};
